import { Alert, Button, cn } from '@alicerce/ui';
import { LogOut, Menu, X } from 'lucide-react';
import { useEffect, useRef, useState, type RefObject } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router';
import { NAV, NAV_FOOTER, STAFF_NAV, type NavItem } from '@/lib/modules';
import { useCurrent, useSession } from '@/lib/session';
import { Logo } from './logo';
import { NotificationBell } from './notification-bell';
import { SetupGuide } from './setup/setup-guide';
import { TenantSwitcher } from './tenant-switcher';

/** Quanto o menu pode passar da altura sem mostrar a barra de rolagem (ainda rola pela roda ou pelo toque). */
export const SMALL_OVERFLOW_PX = 48;

/**
 * Barra de rolagem do menu: fina quando há bastante para rolar e escondida quando sobra só um
 * pedacinho (ou nada), para não aparecer uma barra à toa ao lado do menu.
 */
export function useMenuScrollbar(ref: RefObject<HTMLElement | null>, threshold = SMALL_OVERFLOW_PX) {
  const [hidden, setHidden] = useState(true);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const measure = () => setHidden(el.scrollHeight - el.clientHeight <= threshold);
    measure();
    window.addEventListener('resize', measure);
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(measure);
    observer?.observe(el);
    if (el.firstElementChild) observer?.observe(el.firstElementChild);
    return () => {
      window.removeEventListener('resize', measure);
      observer?.disconnect();
    };
  }, [ref, threshold]);
  return hidden ? 'scrollbar-none' : 'scrollbar-thin';
}

function NavEntry({ item, onNavigate }: { item: NavItem; onNavigate?: () => void }) {
  const { isModuleActive } = useCurrent();
  const locked = item.module ? !isModuleActive(item.module) : false;
  const Icon = item.icon;
  return (
    <NavLink
      to={item.to}
      end={item.to === '/'}
      onClick={onNavigate}
      className={({ isActive }) =>
        cn(
          'group flex items-center gap-2.5 rounded-lg px-2.5 py-1.5 text-sm font-medium transition',
          isActive ? 'bg-brand-600 text-white shadow-[0_8px_20px_-12px_rgb(30_58_95/0.8)]' : 'text-ink/80 hover:bg-white hover:text-ink hover:shadow-[0_1px_3px_rgb(30_58_95/0.08)]',
          locked && !isActive && 'text-muted',
        )
      }
    >
      <Icon className="size-4 shrink-0" aria-hidden />
      <span className="flex-1">{item.label}</span>
      {locked && <span className="rounded-full bg-violet-50 px-2 text-[10px] font-semibold uppercase tracking-wide text-violet">Ativar</span>}
    </NavLink>
  );
}

function Sidebar({ onNavigate }: { onNavigate?: () => void }) {
  const { me } = useSession();
  const { can } = useCurrent();
  const visible = (i: NavItem) => !i.permission || can(i.permission);
  return (
    <nav aria-label="Menu principal" className="flex min-h-full flex-col gap-0.5 p-2.5">
      <div className="mb-1 px-2 pt-1 pb-2">
        <Logo />
      </div>
      <div className="mb-2 rounded-xl bg-mist/70 p-1.5">
        <TenantSwitcher />
      </div>
      {NAV.filter(visible).map((i) => (
        <NavEntry key={i.to} item={i} onNavigate={onNavigate} />
      ))}
      <div className="mt-auto space-y-0.5 border-t border-line/70 pt-2">
        {NAV_FOOTER.filter(visible).map((i) => (
          <NavEntry key={i.to} item={i} onNavigate={onNavigate} />
        ))}
        {me?.user.isPlatformStaff && <NavEntry item={STAFF_NAV} onNavigate={onNavigate} />}
      </div>
    </nav>
  );
}

/** Layout do painel: menu lateral (gaveta no celular), barra superior e avisos do negócio. */
export function Shell() {
  const [open, setOpen] = useState(false);
  const { me, logout, impersonating, selectTenant } = useSession();
  const { tenant } = useCurrent();
  const navigate = useNavigate();
  const menuRef = useRef<HTMLDivElement>(null);
  const drawerRef = useRef<HTMLDivElement>(null);
  const menuScrollbar = useMenuScrollbar(menuRef);
  const drawerScrollbar = useMenuScrollbar(drawerRef);
  return (
    <div className="min-h-dvh lg:grid lg:grid-cols-[15rem_1fr]">
      {/* Menu lateral em vidro claro, flutuando com uma margem da borda da tela. */}
      <aside className="hidden p-3 pr-0 lg:block">
        <div
          ref={menuRef}
          data-testid="menu-lateral"
          className={cn(
            'sticky top-3 h-[calc(100dvh-1.5rem)] overflow-y-auto overscroll-contain rounded-card border border-white bg-white/70 shadow-[0_18px_40px_-28px_rgb(30_58_95/0.45)] backdrop-blur-xl backdrop-saturate-150',
            menuScrollbar,
          )}
        >
          <Sidebar />
        </div>
      </aside>
      {open && (
        <div className="fixed inset-0 z-40 lg:hidden" role="dialog" aria-modal="true" aria-label="Menu">
          <button type="button" aria-label="Fechar menu" className="absolute inset-0 bg-brand-900/30 backdrop-blur-sm" onClick={() => setOpen(false)} />
          <div
            ref={drawerRef}
            className={cn('absolute inset-y-2 left-2 w-64 overflow-y-auto overscroll-contain rounded-card border border-white bg-white/90 shadow-xl backdrop-blur-xl', drawerScrollbar)}
          >
            <div className="flex justify-end p-2">
              <Button variant="ghost" size="sm" aria-label="Fechar menu" onClick={() => setOpen(false)}>
                <X className="size-4" />
              </Button>
            </div>
            <Sidebar onNavigate={() => setOpen(false)} />
          </div>
        </div>
      )}
      <div className="flex min-w-0 flex-col">
        {/* Barra do topo como uma pílula de vidro, no espírito da ilha do iPhone. */}
        <header className="sticky top-3 z-30 mx-3 mt-3 flex h-14 items-center gap-2 rounded-full border border-white bg-white/75 pr-2 pl-3 shadow-[0_12px_32px_-20px_rgb(30_58_95/0.5)] backdrop-blur-xl backdrop-saturate-150 sm:mx-6">
          <Button variant="ghost" size="sm" className="lg:hidden" aria-label="Abrir menu" onClick={() => setOpen(true)}>
            <Menu className="size-5" />
          </Button>
          <span className="truncate text-sm font-medium text-muted lg:hidden">{tenant?.tradeName ?? 'Gestora'}</span>
          <div className="ml-auto flex items-center gap-1">
            <NotificationBell />
            <span className="hidden items-center gap-2 px-2 text-sm text-muted sm:inline-flex">
              <span aria-hidden className="grid size-7 place-items-center rounded-full bg-brand-50 text-xs font-semibold text-brand-700">
                {me?.user.name?.trim().charAt(0).toUpperCase()}
              </span>
              {me?.user.name}
            </span>
            <Button
              variant="ghost"
              size="sm"
              aria-label="Sair"
              title="Sair"
              onClick={async () => {
                await logout();
                navigate('/entrar');
              }}
            >
              <LogOut className="size-4" />
            </Button>
          </div>
        </header>
        <SetupGuide />
        {impersonating && (
          <Alert
            tone="warning"
            className="mx-3 mt-3 sm:mx-6"
            title="Você está vendo este negócio como suporte, só para leitura."
            action={
              <Button
                size="sm"
                variant="secondary"
                onClick={() => {
                  selectTenant(null);
                  navigate('/interno');
                }}
              >
                Sair do modo suporte
              </Button>
            }
          >
            O acesso fica registrado e o cliente pode vê-lo.
          </Alert>
        )}
        {tenant?.status === 'READ_ONLY' && !impersonating && (
          <Alert
            tone="danger"
            className="mx-3 mt-3 sm:mx-6"
            title="Seu painel está em modo somente leitura."
            action={
              <Button size="sm" onClick={() => navigate('/assinatura')}>
                Regularizar assinatura
              </Button>
            }
          >
            Seus dados estão guardados. Regularize a assinatura para voltar a editar.
          </Alert>
        )}
        <main className="mx-auto w-full max-w-7xl flex-1 px-4 py-6 sm:px-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
